import { createPortal } from 'react-dom'
import {
  DocumentFileEntry,
  DocumentType,
  getDocumentTypeLabel,
} from '@/services/api/stockDispatch'

interface DispatchDocumentFilesModalProps {
  isOpen: boolean
  onClose: () => void
  documentType: DocumentType
  files: DocumentFileEntry[]
  onViewFile: (fileId: string) => void
  onAddFiles?: () => void
  onDeleteFile?: (fileId: string) => void
  deletingFileId?: string | null
}

const DispatchDocumentFilesModal = ({
  isOpen,
  onClose,
  documentType,
  files,
  onViewFile,
  onAddFiles,
  onDeleteFile,
  deletingFileId,
}: DispatchDocumentFilesModalProps) => {
  if (!isOpen) return null

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      onClick={onClose}
      style={{ zIndex: 80 }}
    >
      <div
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '420px',
          maxWidth: '92vw',
          maxHeight: '80vh',
          borderRadius: '12px',
        }}
      >
        <div
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0"
          style={{ padding: '16px 20px' }}
        >
          <h2
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#212126',
            }}
          >
            {getDocumentTypeLabel(documentType)}
          </h2>
          <button onClick={onClose} className="hover:opacity-80" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10.5" stroke="#212126" strokeWidth="1.5" />
              <line x1="9.17" y1="9.17" x2="14.83" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="14.83" y1="9.17" x2="9.17" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" style={{ padding: '16px 20px' }}>
          <p
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '20px',
              color: '#69697A',
              marginBottom: '12px',
            }}
          >
            {files.length} file{files.length === 1 ? '' : 's'} attached. Click View to open or download.
          </p>
          <div className="space-y-2">
            {files.map((file, index) => (
              <div
                key={file.id}
                className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg"
                style={{ padding: '10px 12px' }}
              >
                <div className="min-w-0">
                  <p
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 500,
                      fontSize: '13px',
                      lineHeight: '20px',
                      color: '#212126',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file.name || `File ${index + 1}`}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => onViewFile(file.id)}
                    className="hover:opacity-90 transition-opacity"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '20px',
                      color: '#13AB5B',
                      backgroundColor: '#E8F7EF',
                      border: '1px solid #13AB5B',
                      borderRadius: '6px',
                      padding: '6px 12px',
                    }}
                  >
                    View
                  </button>
                  {onDeleteFile && (
                    <button
                      onClick={() => onDeleteFile(file.id)}
                      disabled={deletingFileId === file.id}
                      className="hover:opacity-90 transition-opacity disabled:opacity-50"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '20px',
                        color: '#B42318',
                        backgroundColor: '#FEF3F2',
                        border: '1px solid #FDA29B',
                        borderRadius: '6px',
                        padding: '6px 12px',
                      }}
                    >
                      {deletingFileId === file.id ? 'Removing…' : 'Remove'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className="flex gap-3 border-t border-gray-200 flex-shrink-0"
          style={{ padding: '16px 20px' }}
        >
          {onAddFiles && (
            <button
              onClick={onAddFiles}
              className="flex-1 py-2 px-4 rounded-lg hover:opacity-90 transition-opacity"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontSize: '14px',
                color: '#212126',
                backgroundColor: '#FFFFFF',
                border: '1px solid #212126',
              }}
            >
              Add files
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 py-2 px-4 rounded-lg hover:opacity-90 transition-opacity"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontSize: '14px',
              color: '#FFFFFF',
              backgroundColor: '#39553A',
              border: '1px solid #39553A',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default DispatchDocumentFilesModal
